import React from 'react';
import {
  Box,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Container,
  Chip
} from '@mui/material';
import RoomIcon from '@mui/icons-material/Room';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import adCenterApi from '../adCenterApi';
import { tokenCookie } from '../SystemHandler/AuthRedirectHandler';
import { showToast, WEBSERVICE, showNextStepPrompt } from '../CoreDataService';
import { useNavigate } from 'react-router-dom';

export default function AudienceTargetingForm() {
  const [profileType, setProfileType] = React.useState('');
  const [keywords, setKeywords] = React.useState([]);
  const [keywordInput, setKeywordInput] = React.useState('');
  const [gender, setGender] = React.useState('any');
  const [ageOption, setAgeOption] = React.useState('all');
  const [minAge, setMinAge] = React.useState('');
  const [maxAge, setMaxAge] = React.useState('');
  const [role, setRole] = React.useState('');
  const [audienceType, setAudienceType] = React.useState('');
  const [operatingSystem, setOperatingSystem] = React.useState('All');
  const [location, setLocation] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [audienceEstimate, setAudienceEstimate] = React.useState(0);
  const [nameOfAudience, setNameOfAudience] = React.useState('');
  const [countryOptions, setCountryOptions] = React.useState([]);
  const [selectedCountries, setSelectedCountries] = React.useState([]);
  const [audienceCode, setAudienceCode] = React.useState('0');
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const navigate = useNavigate();

  // Helper to reset form
  const resetForm = () => {
    setProfileType('');
    setKeywords([]);
    setKeywordInput('');
    setGender('any');
    setAgeOption('all');
    setMinAge('');
    setMaxAge('');
    setRole('');
    setAudienceType('');
    setOperatingSystem('All');
    setLocation('');
    setErrors({});
    setNameOfAudience('');
    setSelectedCountries([]);
    setIsSubmitted(false);
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!nameOfAudience.trim()) {
      newErrors.nameOfAudience = 'Please enter a name for the audience.';
    }

    // Gender validation
    if (!gender) {
      newErrors.gender = 'Please select a gender.';
    }

    // Age validation
    if (ageOption === 'range') {
      if (!minAge) {
        newErrors.minAge = 'Enter min age.';
      } else if (Number(minAge) < 18) {
        newErrors.minAge = 'Min age must be at least 18.';
      }

      if (!maxAge) {
        newErrors.maxAge = 'Enter max age.';
      } else if (Number(maxAge) > 99) {
        newErrors.maxAge = 'Max age must be 99 or less.';
      }

      if (minAge && maxAge && Number(minAge) > Number(maxAge)) {
        newErrors.minAge = 'Min age cannot be greater than max age.';
        newErrors.maxAge = 'Max age cannot be less than min age.';
      }
    }

    // Audience type validation
    if ((role === 'Trader' || role === 'Both') && !audienceType) {
      newErrors.audienceType = 'Select an audience type.';
    }

    // Operating system validation
    if (!operatingSystem) {
      newErrors.operatingSystem = 'Please select an operating system.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAudienceChange = (e) => {
    const value = e.target.value;
    setAudienceType(value);

    // Map string to numeric code
    let code;
    if (value === "Both") code = 0;
    else if (value === "") code = 0;
    else if (value === "Individual") code = 1;
    else if (value === "Enterprise") code = 2;

    setAudienceCode(code);
  };

  // Handle keyword input
  const handleKeywordInput = (e) => {
    setKeywordInput(e.target.value);
  };

  // Add keyword when Enter is pressed
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && keywordInput.trim()) {
      e.preventDefault();
      const newKeyword = keywordInput.trim();
      if (!keywords.includes(newKeyword)) {
        setKeywords([...keywords, newKeyword]);
      }
      setKeywordInput('');
    }
  };

  // Remove keyword
  const removeKeyword = (keywordToRemove) => {
    setKeywords(keywords.filter(keyword => keyword !== keywordToRemove));
  };

  const hasFormErrors = () => {
    return Object.values(errors).some(error => error !== undefined && error !== '');
  };

  const handleSave = async () => {
    setIsSubmitted(true);
    if (!validateForm()) return;
    setLoading(true);
    
    // Handle location - send empty string if Global is selected
    let locationValue = null;
    if (selectedCountries.length > 0) {
      if (selectedCountries.some(c => c.id === "global")) {
        locationValue = ""; // Send empty string for Global
      } else {
        locationValue = selectedCountries.map(country => country.name).join(', ');
      }
    }

    const keywordsString = keywords.join(', ');

    const payload = {
      adminUser: localStorage.getItem('uuid'),
      brokerId: localStorage.getItem('brokerId'),
      audienceName: nameOfAudience.trim(),
      campaignType: null,
      gender: gender === 'any' ? 'Any' : gender.charAt(0).toUpperCase() + gender.slice(1),
      minAge: ageOption === 'all' ? 0 : minAge ? Number(minAge) : 0,
      maxAge: ageOption === 'all' ? 0 : maxAge ? Number(maxAge) : 0,
      location: locationValue,
      role: profileType || null,
      audienceType: (role === 'Trader' || role === 'Both') ? audienceType || null : null,
      deviceType: operatingSystem === 'All' ? 'All' : operatingSystem,
      interests: keywordsString || null
    };

    try {
      const response = await adCenterApi.post('/createAudience', payload);
      if (response.data && response.data.statusCode === 0) {
        showNextStepPrompt('audience', navigate);
        resetForm();
      } else {
        showToast('error', response.data.message || 'Failed to create audience');
      }
    } catch (err) {
      showToast('error', 'Error creating audience');
    } finally {
      setLoading(false);
    }
  };

  // Fetch audience estimate
  const fetchAudienceEstimate = async (genderParam) => {
    try {
      // Handle location - send empty string if Global is selected
      let locationValue = null;
      if (selectedCountries.length > 0) {
        if (selectedCountries.some(c => c.id === "global")) {
          locationValue = ""; // Send empty string for Global
        } else {
          locationValue = selectedCountries.map(country => country.name).join(', ');
        }
      }
      
      const keywordsString = keywords.join(', ');

      const requestPayload = {
        adminUser: localStorage.getItem('uuid'),
        brokerId: localStorage.getItem('brokerId'),
        campaignType: null,
        deviceType: "All",
        location: locationValue,
        gender: genderParam,
        maxAge: maxAge ? Number(maxAge) : 0,
        minAge: minAge ? Number(minAge) : 0,
        role: profileType,
        audienceType: audienceCode,
        interests: keywordsString
      };

      const response = await adCenterApi.post('/getEstimatedAudience', requestPayload);
      setAudienceEstimate(response.data.audienceEstimate);
    } catch (err) {
      setAudienceEstimate(null);
    }
  };

  const fetchCountries = async () => {
    try {
      const token = tokenCookie.get() || localStorage.getItem('access_token_us');
      const response = await axios.get(
        `https://institutional-bo.paybito.com:8443/BrokerAdminApi/broker/getExchangeCountries/${localStorage.getItem('brokerId')}`,
        {
          headers: {
            authorization: `bearer ${token}`,
          },
        }
      );

      if (response.data && response.data.countries) {
        setCountryOptions(response.data.countries);
      }
    } catch (err) {
      console.error('Error fetching countries:', err);
    }
  };

  // On mount and when relevant fields change
  React.useEffect(() => {
    let apiGender = gender;
    if (gender === 'any') apiGender = 'Any';
    else if (gender === 'women') apiGender = 'Women';
    else if (gender === 'men') apiGender = 'Men';
    fetchAudienceEstimate(apiGender);
    fetchCountries();
  }, [gender, minAge, maxAge, profileType, audienceType, selectedCountries, keywords]);

  React.useEffect(() => {
    if (isSubmitted) {
      validateForm();
    }
  }, [nameOfAudience, gender, ageOption, minAge, maxAge, selectedCountries, profileType, audienceType, operatingSystem, isSubmitted]);

  return (
    <Container maxWidth="lg" sx={{ mb: 6 }}>
      {/* Header Section - Full Width */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 1 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" color="primary.main">
            Create new campaign audience
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={0.5} sx={{ fontStyle: 'italic' }}>
            Define your audience targeting criteria to maximize your campaign's effectiveness.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          sx={{ fontWeight: 'bold', textTransform: 'none', boxShadow: 2 }}
          onClick={() => navigate('/viewAudience')}
        >
          View all audiences
        </Button>
      </Box>

      {/* Form Content - Properly Aligned */}
      <Box
        sx={{
          display: 'flex',
          mt: 3,
          gap: { xs: 1, md: 2 },
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Left Panel (Fixed Width) */}
        <Box sx={{ flex: '1 1 60%', maxWidth: { md: '60%' } }}>
          <Card elevation={4} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>

              <Box mb={3}>
                <Typography variant="h5" fontWeight="bold" color="text.primary" gutterBottom>
                  PayBito Whizzo <span style={{ fontWeight: 'normal', fontSize: '0.8em', color: 'text.secondary' }}>⚡ Auto Targeting Powered by AI</span>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your ads automatically target audiences relevant to your ad campaign, minimizing manual work.
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box mt={2}>
                <TextField
                  fullWidth
                  label="Name of the Audience"
                  value={nameOfAudience}
                  onChange={(e) => setNameOfAudience(e.target.value)}
                  variant="outlined"
                  error={!!errors.nameOfAudience}
                  helperText={errors.nameOfAudience || 'This name will help you identify the audience group.'}
                />
              </Box>

              {/* Profile Type Field (Replaces Role) */}
              <Box mt={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="profile-type-label">Who would you like to show your post to? (Optional)</InputLabel>
                  <Select
                    labelId="profile-type-label"
                    id="profile-type-select"
                    value={profileType}
                    label="Who would you like to show your post to? (Optional)"
                    onChange={(e) => setProfileType(e.target.value)}
                  >
                    <MenuItem value="">Select Any</MenuItem>
                    <MenuItem value="Personal & business profiles">Personal & Business Profiles</MenuItem>
                    <MenuItem value="Business profiles">Business Profiles</MenuItem>
                    <MenuItem value="Personal profiles">Personal Profiles</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Keywords Field */}
              <Box mt={4}>
                <TextField
                  fullWidth
                  label="Interested in (Optional)"
                  value={keywordInput}
                  onChange={handleKeywordInput}
                  onKeyPress={handleKeyPress}
                  variant="outlined"
                  helperText="Type keywords and press Enter to add. Example: NFT, Startups, Trading"
                />

                {/* Keywords Chips */}
                <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                  {keywords.map((keyword, index) => (
                    <Chip
                      key={index}
                      label={keyword}
                      onDelete={() => removeKeyword(keyword)}
                      color="primary"
                      variant="outlined"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: 'primary.light',
                        px: 2,
                        py: 0.5,
                        borderRadius: 20,
                        color: 'white',
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Gender */}
              <Box mt={4}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>Gender</Typography>
                <ToggleButtonGroup
                  value={gender}
                  exclusive
                  onChange={(e, val) => val && setGender(val)}
                  fullWidth
                  color="primary"
                  sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }}
                >
                  <ToggleButton value="any">Any</ToggleButton>
                  <ToggleButton value="women">Women</ToggleButton>
                  <ToggleButton value="men">Men</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Age */}
              <Box mt={4}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>Age</Typography>
                <ToggleButtonGroup
                  value={ageOption}
                  exclusive
                  onChange={(e, val) => val && setAgeOption(val)}
                  color="primary"
                  sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }}
                >
                  <ToggleButton value="all">All Ages</ToggleButton>
                  <ToggleButton value="range">Specific Range</ToggleButton>
                </ToggleButtonGroup>

                {ageOption === 'range' && (
                  <Box display="flex" gap={2} mt={2}>
                    <TextField
                      fullWidth
                      label="Min Age"
                      value={minAge}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*$/.test(value)) {
                          setMinAge(value);
                        }
                      }}
                      onBlur={() => {
                        if (!minAge) {
                          setErrors({ ...errors, minAge: "Enter min age." });
                        } else if (Number(minAge) < 18) {
                          setErrors({ ...errors, minAge: "Min age must be at least 18." });
                        } else if (maxAge && Number(minAge) > Number(maxAge)) {
                          setErrors({ ...errors, minAge: "Min age cannot be greater than max age." });
                        } else {
                          const newErrors = { ...errors };
                          delete newErrors.minAge;
                          setErrors(newErrors);
                        }
                      }}
                      variant="outlined"
                      size="small"
                      type="number"
                      InputProps={{ inputProps: { min: 18, max: 99 } }}
                      error={!!errors.minAge}
                      helperText={errors.minAge || "e.g., 18"}
                    />

                    <TextField
                      fullWidth
                      label="Max Age"
                      value={maxAge}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*$/.test(value)) {
                          setMaxAge(value);
                        }
                      }}
                      onBlur={() => {
                        if (!maxAge) {
                          setErrors({ ...errors, maxAge: "Enter max age." });
                        } else if (Number(maxAge) > 99) {
                          setErrors({ ...errors, maxAge: "Max age must be 99 or less." });
                        } else if (minAge && Number(minAge) > Number(maxAge)) {
                          setErrors({ ...errors, maxAge: "Max age cannot be less than min age." });
                        } else {
                          const newErrors = { ...errors };
                          delete newErrors.maxAge;
                          setErrors(newErrors);
                        }
                      }}
                      variant="outlined"
                      size="small"
                      type="number"
                      InputProps={{ inputProps: { min: 18, max: 99 } }}
                      error={!!errors.maxAge}
                      helperText={errors.maxAge || "e.g., 65"}
                    />
                  </Box>
                )}
              </Box>

              {/* Country Selector */}
              <Box mt={4}>
                <FormControl fullWidth>
                  <InputLabel id="country-label">Select Country</InputLabel>
                  <Select
                    labelId="country-label"
                    value=""
                    onChange={(e) => {
                      const countryId = e.target.value;
                      
                      // Handle Global selection
                      if (countryId === "global") {
                        // Clear any selected countries and add Global as a special selection
                        setSelectedCountries([{ 
                          id: "global", 
                          name: "Global (All Countries)", 
                          value: "" 
                        }]);
                      } else {
                        // Handle regular country selection
                        const country = countryOptions.find((c) => c.id === countryId);
                        if (country) {
                          // If Global is currently selected, remove it
                          if (selectedCountries.some(c => c.id === "global")) {
                            setSelectedCountries([{ 
                              id: country.id, 
                              name: country.country, 
                              value: country.country 
                            }]);
                          } else {
                            setSelectedCountries([...selectedCountries, { 
                              id: country.id, 
                              name: country.country, 
                              value: country.country 
                            }]);
                          }
                        }
                      }
                    }}
                    label="Select Country"
                  >
                    <MenuItem key="global" value="global">
                      Global (All Countries)
                    </MenuItem>
                    {countryOptions
                      .filter((country) => {
                        // Don't show countries that are already selected
                        // Don't show any countries if Global is selected
                        if (selectedCountries.some(c => c.id === "global")) {
                          return false;
                        }
                        return !selectedCountries.some((c) => c.id === country.id);
                      })
                      .map((country) => (
                        <MenuItem key={country.id} value={country.id}>
                          {country.country}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {/* Chips Display */}
                <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                  {selectedCountries.map((country) => (
                    <Box
                      key={country.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: country.id === "global" ? 'secondary.main' : 'primary.light',
                        px: 2,
                        py: 0.5,
                        borderRadius: 20,
                        color: 'white',
                      }}
                    >
                      {country.name}
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (country.id === "global") {
                            // Clear all selections when removing Global
                            setSelectedCountries([]);
                          } else {
                            setSelectedCountries((prev) => prev.filter((c) => c.id !== country.id));
                          }
                        }}
                        sx={{ color: 'white', ml: 1 }}
                      >
                        ×
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Operating System */}
              <Box mt={4}>
                <FormControl fullWidth variant="outlined" error={!!errors.operatingSystem}>
                  <InputLabel id="os-label">Operating System</InputLabel>
                  <Select
                    labelId="os-label"
                    id="os-select"
                    value={operatingSystem}
                    label="Operating System"
                    onChange={(e) => setOperatingSystem(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Desktop">Desktop</MenuItem>
                    <MenuItem value="Android">Android</MenuItem>
                    <MenuItem value="IOS">iOS</MenuItem>
                  </Select>
                  {errors.operatingSystem && <Typography color="error" variant="caption">{errors.operatingSystem}</Typography>}
                </FormControl>
              </Box>

              {/* Save Button */}
              <Box mt={6}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={handleSave}
                  sx={{ textTransform: 'uppercase', py: 1.5 }}
                  disabled={loading || hasFormErrors()}
                  startIcon={loading ? <span style={{ display: 'flex', alignItems: 'center' }}><svg style={{ marginRight: 8 }} width="20" height="20" viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="5" strokeDasharray="31.4 31.4" strokeLinecap="round"><animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" /></circle></svg></span> : null}
                >
                  {loading ? 'Saving...' : 'Save Audience Settings'}
                </Button>
              </Box>

            </CardContent>
          </Card>
        </Box>

        {/* Right Panel (Audience Estimate) */}
        <Box sx={{ flex: '1 1 40%', maxWidth: { md: '40%' } }}>
          <Card elevation={4} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                  Audience Estimate
                </Typography>
                <Tooltip title="The audience estimate is calculated based on your selections across demographics, devices, custom audiences and targeting features. It's not affected by your campaign's budget or placement settings. It's representative of the size of the audience that might receive your ad, not an exact count of people who would see it.">
                  <IconButton size="small" color="info">
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Based on your targeting selections, this is the estimated size of your potential audience.
              </Typography>
              <Typography variant="h4" color="primary" fontWeight="bold" mt={2}>
                {audienceEstimate !== null
                  ? `${audienceEstimate.toFixed(2)}K–${(audienceEstimate * 1.1).toFixed(2)}K`
                  : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={1}>
                (Estimated daily reach)
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container>
  );
}